import { Button } from '@readcloud/component-library/components/Base/Button';
import {
  BSForm,
  Form,
  Input,
  Label,
} from '@readcloud/component-library/components/Base/Form';
import {
  Modal,
  ModalBody,
  ModalFooter,
} from '@readcloud/component-library/components/Base/Modal';
import { Tooltip, TooltipTrigger, TooltipContent } from '@readcloud/ui';

import C from 'classnames';

import React, { FormEvent } from 'react';

import { PageLabel, PageLabelList } from '@readcloud/data';

import { MdChevronLeft, MdChevronRight } from 'react-icons/md';
import { LoadingSection } from '../../Common/Base/LoadingSection';

interface PageLabelErrorDialogProps {
  text: string;
  onClose: () => void;
}

const PageLabelErrorDialog: React.FC<PageLabelErrorDialogProps> = (props) => {
  const [open, setOpen] = React.useState<boolean>(true);

  const handleClose = () => {
    setOpen(false);
    props.onClose();
  };

  return (
    <Modal toggle={handleClose} isOpen={open}>
      <ModalBody>
        <p>{props.text}</p>
      </ModalBody>
      <ModalFooter>
        <Button onClick={handleClose}>Ok</Button>
      </ModalFooter>
    </Modal>
  );
};

interface PageLabelInputDialogProps {
  onSubmit: (text: string) => void;
  onClose: () => void;
}

const PageLabelInputDialog: React.FC<PageLabelInputDialogProps> = (props) => {
  const [text, setText] = React.useState<string>('');
  const [open, setOpen] = React.useState<boolean>(true);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setText(e.currentTarget.value);
  };
  const handleClose = () => {
    setOpen(false);
    props.onClose();
  };

  const handleSubmit = () => {
    if (text && text.trim().length) {
      props.onSubmit(text.trim());
    }
  };

  return (
    <Modal isOpen={open} toggle={handleClose}>
      <ModalBody>
        <Label>"Enter a page"</Label>
        <Input name="navigate-to-page" value={text} onChange={handleChange} />
      </ModalBody>
      <ModalFooter>
        <Button onClick={handleClose}>Cancel</Button>
        <Button variant="primary" onClick={handleSubmit}>
          Go
        </Button>
      </ModalFooter>
    </Modal>
  );
};

interface PageLabelInputProps {
  onChange: (valueIndex: number) => void;
  dataSource: PageLabelList;
  valueIndex: number;
  onBlur: () => void;
  onFocus: () => void;
}

const inputStyle = {
  maxWidth: '8rem',
};

const PageLabelInput: React.FC<PageLabelInputProps> = (props) => {
  const inputEl = React.useRef<HTMLInputElement>(null);

  const [focused, setFocused] = React.useState<boolean>(false);
  const [text, setText] = React.useState<string>(
    props.dataSource[props.valueIndex]?.label || ''
  );
  const [dialog, setDialog] = React.useState<React.ReactNode>(undefined);
  const [error, setError] = React.useState<string>('');
  const handleInputDialogClose = () => {
    setDialog(undefined);
  };

  const showLabelInputDialog = () => {
    setDialog(
      <PageLabelInputDialog
        onClose={handleInputDialogClose}
        onSubmit={submit}
      />
    );
  };

  const submit = (text: string) => {
    const foundIndex = props.dataSource.findIndex(
      (i) => i.label.toLowerCase() === text.toLowerCase()
    );
    if (foundIndex > -1) {
      const page = props.dataSource[foundIndex];
      props.onChange(foundIndex);
      setText(page.label);
    } else {
      setError(`Page label '${text}' could not be found`);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setText(e.currentTarget.value);
  };

  const handleKeyUp = (e: React.KeyboardEvent<HTMLDivElement>) => {
    e.preventDefault();
    submit(text.trim());
  };

  const submitForm = (e: FormEvent<Element>) => {
    e.preventDefault();
    submit(text.trim());
  };

  React.useEffect(() => {
    setText(props.dataSource[props.valueIndex]?.label || '');
  }, [props.valueIndex]);

  const handleBlur = () => {
    const found = props.dataSource[props.valueIndex];
    if (found) {
      setText(found.label);
    }
  };

  const handleErrorClosed = () => {
    setError(undefined);
  };

  const handlePointerUp = (e: React.PointerEvent<HTMLInputElement>) => {
    if (e.pointerType === 'touch' || e.pointerType === 'pen') {
      e.currentTarget.blur();
      showLabelInputDialog();
    }
  };

  return (
    <div data-testid={'divPageLabel_' + text}>
      <Form onSubmit={submitForm}>
        <Input
          name="navigate"
          onBlur={handleBlur}
          style={inputStyle}
          onClick={(e) =>
            handlePointerUp(e as React.PointerEvent<HTMLInputElement>)
          }
          value={text}
          onChange={handleChange}
          className="rc-min-w-[4rem]"
        />
      </Form>
      {error ? (
        <PageLabelErrorDialog onClose={handleErrorClosed} text={error} />
      ) : undefined}
    </div>
  );
};

interface Props {
  next: () => void;
  previous: () => void;
  onChange: (pageLabel: PageLabel) => void;
  valueIndex: number;
  max: number;
  pageLabels: PageLabelList;
  loading?: boolean;
  pageChangeButtonsClassName: string;
  pageSeekClassName: string;
}

interface State {
  valueIndex: number;
}
export const PageNavigation: React.FC<Props> = ({
  next,
  previous,
  max,
  pageLabels,
  loading,
  ...props
}) => {
  const [valueIndex, setValueIndex] = React.useState<number>(
    props.valueIndex || 0
  );

  const [showPrevTooltip, setShowPrevTooltip] = React.useState<boolean>(false);
  const prevButtonRef = React.useRef();
  const [showNextTooltip, setShowNextTooltip] = React.useState<boolean>(false);
  const nextButtonRef = React.useRef();

  React.useEffect(() => {
    setValueIndex(props.valueIndex);
  }, [props.valueIndex, pageLabels]);

  const handlePageLabelInputChange = (valueIndex: number) => {
    setValueIndex(valueIndex);
    if (props.onChange && pageLabels[valueIndex]) {
      props.onChange(pageLabels[valueIndex]);
    }
  };

  const handleChange = (valueIndex: number) => setValueIndex(valueIndex);

  const handleAfterChange = () => {
    if (props.onChange && pageLabels[valueIndex]) {
      props.onChange(pageLabels[valueIndex]);
    }
  };

  const handlePageLabelInputBlur = () => {
    //
  };
  const handlePageLabelInputFocus = () => {
    //
  };

  return (
    <React.Fragment>
      {!loading ? (
        <>
          <PageNavigationSlider
            className={props.pageSeekClassName}
            max={max}
            value={valueIndex + 1}
            onChange={(value) => handleChange(value - 1)}
            onAfterChange={handleAfterChange}
          />
          <div
            data-testid="divPageNavigation"
            className={C(
              'rc-flex rc-items-center',
              props.pageChangeButtonsClassName
            )}
          >
            <Tooltip>
              <TooltipTrigger>
                <Button
                  icon
                  variant="light"
                  onClick={() => {
                    previous();
                  }}
                >
                  <MdChevronLeft size="28" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Previous Page</TooltipContent>
            </Tooltip>

            <div className="rc-px-1" />

            <React.Fragment>
              {/* onBlue, onFocus are both empty for PageLabelInput >.> */}
              <PageLabelInput
                onChange={handlePageLabelInputChange}
                valueIndex={valueIndex}
                dataSource={pageLabels}
                onBlur={handlePageLabelInputBlur}
                onFocus={handlePageLabelInputFocus}
              />
              <div className="rc-p-1" />
              <span className="rc-whitespace-nowrap">
                {`(${valueIndex + 1} of ${max})`}
              </span>
            </React.Fragment>

            <div className="rc-px-1" />

            <Tooltip>
              <TooltipTrigger>
                <Button
                  icon
                  variant="light"
                  onClick={() => {
                    next();
                  }}
                >
                  <MdChevronRight size="28" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Next Page</TooltipContent>
            </Tooltip>
          </div>
        </>
      ) : (
        <>
          <PageNavigationSlider
            className={props.pageSeekClassName}
            max={0}
            value={0}
            disabled
          />
          <LoadingSection height="43px" width="200px" />
        </>
      )}
    </React.Fragment>
  );
};

interface PageNavigationSliderProps {
  min?: number;
  max: number;
  step?: number;
  value: number;
  onChange?: (value: number) => void;
  onAfterChange?: (value: number) => void;
  className: string;
  disabled?: boolean;
}
export const PageNavigationSlider: React.FC<PageNavigationSliderProps> = ({
  step = 1,
  min = 1,
  ...props
}) => {
  return (
    <BSForm.Range
      style={{ opacity: 1 }}
      className={C('page-navigation-slider form-range', props.className)}
      min={min}
      max={props.max}
      value={props.value}
      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
        props.onChange(e.target.valueAsNumber);
      }}
      onPointerUp={(e: React.MouseEvent<HTMLInputElement>) => {
        if (e.target instanceof HTMLInputElement)
          props.onAfterChange(e.target.valueAsNumber);
      }}
    />
  );
};
